const content = `
# Topologies

A **topology** is a power system architecture that defines how solar panels, batteries, chargers, and the grid interconnect. The key difference between topologies is **coupling** — whether components share a DC bus or route through AC.

## AC vs DC Coupling

**AC-coupled** systems connect all components through an AC bus (typically 480 V). PV uses a string inverter to convert DC to AC, and the EV charger rectifies AC back to DC. Each conversion loses energy.

**DC-coupled** systems connect PV, BESS, and DC fast chargers on a shared high-voltage DC bus (typically 800 V or 1000 V). PV power reaches the EV battery with just one DC-DC conversion instead of two lossy AC round-trips.

| Path | AC-Coupled Conversions | DC-Coupled Conversions |
|------|----------------------|----------------------|
| PV to EV | 2 (DC-AC, AC-DC) | 1 (DC-DC) |
| PV to BESS | 2 (DC-AC, AC-DC) | 1 (DC-DC) |
| BESS to EV | 2 (DC-AC, AC-DC) | 1 (DC-DC) |
| Grid to EV | 1 (AC-DC) | 2 (AC-DC, DC-DC) |

## The 12 Presets

| # | Name | DC Bus | AC Bus | PV-EV Eff. | Use Case |
|---|------|--------|--------|-----------|----------|
| 1 | Full AC-Coupled | -- | 480 V | 90.2% | Legacy installations |
| 2 | Full DC-Coupled | 800 V | 480 V | 99.0% | New builds, max efficiency |
| 3 | Hybrid AC/DC | 800 V | 480 V | 95.0% | Mixed fleet + AC loads |
| 4 | DC Bus + AC Chargers | 800 V | 480 V | 93.0% | Existing AC charger reuse |
| 5 | PV-Direct DC | 800 V | 480 V | 99.0% | Ultra-low-loss PV to EV |
| 6 | BESS-Centric DC | 800 V | 480 V | 97.0% | Peak shaving focused |
| 7 | Split DC Bus | 800/400 V | 480 V | 97.0% | Mixed voltage chargers |
| 8 | DC Microgrid | 800 V | 480 V | 99.0% | Island-capable sites |
| 9 | AC Microgrid | -- | 480 V | 90.2% | Grid-forming inverter |
| 10 | High-Voltage DC | 1000 V | 480 V | 99.2% | Utility-scale depots |
| 11 | Modular DC Blocks | 800 V | 480 V | 99.0% | Scalable deployments |
| 12 | DC + V2G | 800 V | 480 V | 98.0% | Vehicle-to-grid capable |

## How to Browse and Select

1. Open the **Topologies** section from the left navigation.
2. Click any topology row to expand its detail panel showing efficiency ratings, architecture info, and a description.
3. Click **Select & Continue** to proceed to simulation with that topology.
4. To compare two topologies, click **Compare Mode** in the top-right, check two rows, then click **Compare These Topologies**.
`;

export default content;
